import { useEffect } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

/**
 * React Router keeps the window scroll position across navigations, so following
 * a link from halfway down a long page lands you halfway down the next one — or
 * pinned to its bottom if it is shorter. Reset on every push/replace.
 *
 * Two things make the naive "scrollTo(0,0) in an effect" flaky here, and both
 * are handled below:
 *
 *  1. Routes are React.lazy + <Suspense> and the router runs navigation inside
 *     startTransition (v7_startTransition). The destination page can commit a
 *     frame or two after the URL changes, so a single synchronous scroll can
 *     fire before the tall new page is laid out. We scroll now AND after the
 *     next paint.
 *  2. The browser's own scroll restoration ('auto') tries to put the previous
 *     offset back on history moves and can win the race. We take manual control.
 *
 * Back/forward (POP) is left to the browser-less manual restore of "top" too —
 * simpler and predictable; a hash target wins over everything.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation()
  const navigationType = useNavigationType()

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
  }, [])

  useEffect(() => {
    if (hash) {
      // Lazy routes mount after the URL changes, so the anchor may not exist
      // yet; try now, then again after the next paint.
      const jump = () => document.getElementById(hash.slice(1))?.scrollIntoView()
      jump()
      const raf = window.requestAnimationFrame(jump)
      return () => window.cancelAnimationFrame(raf)
    }

    const toTop = () => window.scrollTo(0, 0)
    toTop()
    // Second pass after the deferred/lazy commit paints, so a tall page that
    // arrives a frame late still lands at the top.
    const raf = window.requestAnimationFrame(toTop)
    return () => window.cancelAnimationFrame(raf)
  }, [pathname, hash, navigationType])

  return null
}
